// src/components/Predicciones2.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Stack,
  Divider,
  Chip,
  Alert,
  Grid,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BadgeIcon from "@mui/icons-material/Badge";
import CakeIcon from "@mui/icons-material/Cake";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";

// ===================== Helpers =====================
function onlyLettersSpaces(s = "") {
  return s.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, "").replace(/\s+/g, " ").trim();
}
function onlyDigits(s = "") {
  return s.replace(/\D/g, "");
}

// Hash simple y estable (no criptográfico)
function hashString(str) {
  let h = 2166136261; // FNV-ish
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h >>> 0);
}
function twoDigits(n) {
  return String(n).padStart(2, "0");
}

function makeNumbersFromSeed(seedStr, count, min = 0, max = 99) {
  const seed = hashString(seedStr);
  const range = max - min + 1;

  const nums = [];
  let x = seed || 123456789;

  for (let i = 0; i < count; i++) {
    // LCG
    x = (Math.imul(1664525, x) + 1013904223) >>> 0;
    const val = min + (x % range);
    nums.push(val);
  }

  // quitar duplicados manteniendo orden
  const unique = [];
  const seen = new Set();
  for (const n of nums) {
    const k = String(n);
    if (!seen.has(k)) {
      seen.add(k);
      unique.push(n);
    }
  }

  // si quedaron menos por duplicados, rellenar
  let j = 0;
  while (unique.length < count && j < 200) {
    x = (Math.imul(22695477, x) + 1) >>> 0;
    const val = min + (x % range);
    const k = String(val);
    if (!seen.has(k)) {
      seen.add(k);
      unique.push(val);
    }
    j++;
  }

  return unique.slice(0, count);
}

function breakdownDOB(isoDate) {
  if (!isoDate || !/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) return null;

  const [yyyy, mm, dd] = isoDate.split("-").map((x) => parseInt(x, 10));
  const year2 = yyyy % 100;

  const sumDigits = (s) =>
    String(s)
      .split("")
      .reduce((a, b) => a + Number(b), 0);

  const lifePath = (() => {
    const s = `${yyyy}${twoDigits(mm)}${twoDigits(dd)}`;
    let t = sumDigits(s);
    while (t > 9) t = sumDigits(t);
    return t;
  })();

  return { day: dd, month: mm, year: yyyy, year2, lifePath };
}

// ===================== Component =====================
export default function Predicciones2() {
  const username = localStorage.getItem("username") || "";
  const phone = localStorage.getItem("phone") || "";
  const isGuest = localStorage.getItem("isGuest") === "1";

  // BLOQUE 1
  const [fullName, setFullName] = useState("");
  const [fatherName, setFatherName] = useState("");
  const [motherName, setMotherName] = useState("");

  // BLOQUE 2
  const [cedula, setCedula] = useState("");

  // BLOQUE 3
  const [dob, setDob] = useState("");

  // Mensaje general
  const [msg, setMsg] = useState({ type: "", text: "" });

  // Resultados mostrados (se revelan después del countdown)
  const [showNameResults, setShowNameResults] = useState(false);
  const [showCedResults, setShowCedResults] = useState(false);
  const [showDobResults, setShowDobResults] = useState(false);

  // Countdown por bloque
  const [nameCountdown, setNameCountdown] = useState(0);
  const [cedCountdown, setCedCountdown] = useState(0);
  const [dobCountdown, setDobCountdown] = useState(0);

  // Para evitar múltiples intervalos
  const timersRef = useRef({
    name: null,
    ced: null,
    dob: null,
  });

  // ======= Seeds y números (deterministas)
  const luckyByName = useMemo(() => {
    const a = onlyLettersSpaces(fullName).toLowerCase();
    const b = onlyLettersSpaces(fatherName).toLowerCase();
    const c = onlyLettersSpaces(motherName).toLowerCase();
    if (!a || !b || !c) return [];
    const seed = `NAME|${a}|F|${b}|M|${c}`;
    return makeNumbersFromSeed(seed, 5, 0, 99).map(twoDigits);
  }, [fullName, fatherName, motherName]);

  const cedDigits = useMemo(() => onlyDigits(cedula), [cedula]);

  const luckyByCedula = useMemo(() => {
    if (cedDigits.length !== 11) return [];
    const seed = `CED|${cedDigits}`;
    return makeNumbersFromSeed(seed, 4, 0, 99).map(twoDigits);
  }, [cedDigits]);

  const dobInfo = useMemo(() => breakdownDOB(dob), [dob]);

  const luckyByDob = useMemo(() => {
    if (!dobInfo) return [];
    const seed = `DOB|${dobInfo.year}-${twoDigits(dobInfo.month)}-${twoDigits(
      dobInfo.day
    )}|LP|${dobInfo.lifePath}`;
    return makeNumbersFromSeed(seed, 3, 0, 99).map(twoDigits);
  }, [dobInfo]);

  // Limpiar intervalos al desmontar
  useEffect(() => {
    return () => {
      Object.values(timersRef.current).forEach((t) => t && clearInterval(t));
    };
  }, []);

  const saveAttempt = async ({ blockType, inputs, results, extra = null }) => {
    // Cada intento crea un registro nuevo (addDoc)
    const payload = {
      createdAt: serverTimestamp(),
      blockType, // "name" | "cedula" | "dob"
      user: { username: username || null, phone: phone || null, isGuest },
      inputs,
      results,
      extra, // opcional (ej: desglose DOB)
    };

    await addDoc(collection(db, "predicciones"), payload);
  };

  const startCountdown = ({ block }) => {
    setMsg({ type: "", text: "" });

    const setCountdown =
      block === "name"
        ? setNameCountdown
        : block === "ced"
        ? setCedCountdown
        : setDobCountdown;

    const getCountdown =
      block === "name"
        ? () => nameCountdown
        : block === "ced"
        ? () => cedCountdown
        : () => dobCountdown;

    // no iniciar si ya está corriendo
    if (getCountdown() > 0) return;

    // ocultar resultados mientras corre
    if (block === "name") setShowNameResults(false);
    if (block === "ced") setShowCedResults(false);
    if (block === "dob") setShowDobResults(false);

    setCountdown(5);

    // limpiar timer previo si existiera
    if (timersRef.current[block]) {
      clearInterval(timersRef.current[block]);
      timersRef.current[block] = null;
    }

    timersRef.current[block] = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          // termina
          clearInterval(timersRef.current[block]);
          timersRef.current[block] = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // ======= Cuando llega a 0, revelamos resultados y guardamos =======
  useEffect(() => {
    const run = async () => {
      // NAME
      if (nameCountdown === 0 && timersRef.current.name === null && showNameResults === false) {
        // Solo si se inició un conteo antes (para no disparar al cargar)
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameCountdown]);

  // Para disparar "post-countdown" correctamente, usamos flags de "último clic"
  const [lastTriggered, setLastTriggered] = useState(null); // "name" | "ced" | "dob" | null

  // Al terminar cada conteo (cuando llega a 0), si fue el último bloque disparado, guardamos y mostramos
  useEffect(() => {
    const afterCountdown = async () => {
      try {
        // NAME
        if (lastTriggered === "name" && nameCountdown === 0) {
          setShowNameResults(true);

          await saveAttempt({
            blockType: "name",
            inputs: {
              fullName: onlyLettersSpaces(fullName) || null,
              fatherName: onlyLettersSpaces(fatherName) || null,
              motherName: onlyLettersSpaces(motherName) || null,
            },
            results: { byName: luckyByName },
          });

          setMsg({ type: "success", text: "Predicción por Nombre guardada ✅" });
          setLastTriggered(null);
        }

        // CED
        if (lastTriggered === "ced" && cedCountdown === 0) {
          setShowCedResults(true);

          await saveAttempt({
            blockType: "cedula",
            inputs: { cedula: cedDigits || null },
            results: { byCedula: luckyByCedula },
          });

          setMsg({ type: "success", text: "Predicción por Cédula guardada ✅" });
          setLastTriggered(null);
        }

        // DOB
        if (lastTriggered === "dob" && dobCountdown === 0) {
          setShowDobResults(true);

          await saveAttempt({
            blockType: "dob",
            inputs: { dob: dob || null },
            results: { byDob: luckyByDob },
            extra: dobInfo
              ? {
                  dobBreakdown: {
                    day: dobInfo.day,
                    month: dobInfo.month,
                    year: dobInfo.year,
                    year2: dobInfo.year2,
                    lifePath: dobInfo.lifePath,
                  },
                }
              : null,
          });

          setMsg({ type: "success", text: "Predicción por Fecha guardada ✅" });
          setLastTriggered(null);
        }
      } catch (e) {
        console.error(e);
        setMsg({ type: "error", text: "No se pudo guardar. Intenta de nuevo." });
        setLastTriggered(null);
      }
    };

    afterCountdown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameCountdown, cedCountdown, dobCountdown]);

  // ======= Validaciones por bloque =======
  const canGenerateName = useMemo(() => {
    const a = onlyLettersSpaces(fullName);
    const b = onlyLettersSpaces(fatherName);
    const c = onlyLettersSpaces(motherName);
    return a.length > 2 && b.length > 2 && c.length > 2;
  }, [fullName, fatherName, motherName]);

  const canGenerateCed = useMemo(() => cedDigits.length === 11, [cedDigits]);
  const canGenerateDob = useMemo(() => !!dobInfo, [dobInfo]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at 12% 18%, rgba(25,118,210,0.18) 0%, transparent 46%), radial-gradient(circle at 88% 82%, rgba(76,175,80,0.14) 0%, transparent 42%), #0b1220",
        py: 5,
      }}
    >
      <Container maxWidth="md">
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 900, color: "white" }}>
            🔮 Predicciones
          </Typography>

          <Typography sx={{ color: "rgba(255,255,255,0.75)", mt: 1 }}>
            Prueba tu suerte: cada intento se guarda como un registro nuevo.
          </Typography>

          <Stack
            direction="row"
            spacing={1}
            justifyContent="center"
            sx={{ mt: 2, flexWrap: "wrap", gap: 1 }}
          >
            <Chip
              label={isGuest ? "Invitado" : "Usuario"}
              sx={{
                color: "white",
                backgroundColor: isGuest
                  ? "rgba(255,152,0,0.35)"
                  : "rgba(76,175,80,0.35)",
                border: "1px solid rgba(255,255,255,0.18)",
              }}
            />
            {username ? (
              <Chip
                label={`@${username}`}
                sx={{
                  color: "white",
                  backgroundColor: "rgba(25,118,210,0.35)",
                  border: "1px solid rgba(255,255,255,0.18)",
                }}
              />
            ) : null}
            {phone ? (
              <Chip
                label={`📱 ${phone}`}
                sx={{
                  color: "white",
                  backgroundColor: "rgba(156,39,176,0.28)",
                  border: "1px solid rgba(255,255,255,0.18)",
                }}
              />
            ) : null}
          </Stack>
        </Box>

        {msg.text ? (
          <Alert severity={msg.type} sx={{ mb: 2 }}>
            {msg.text}
          </Alert>
        ) : null}

        <Grid container spacing={2}>
          {/* BLOQUE 1 */}
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 3, background: "rgba(255,255,255,0.92)", overflow: "hidden" }}>
              <Box
                sx={{
                  p: 2,
                  background:
                    "linear-gradient(135deg, rgba(25,118,210,1) 0%, rgba(156,39,176,1) 100%)",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <AutoAwesomeIcon />
                  <Typography sx={{ fontWeight: 900 }}>Por tu Nombre & Familia</Typography>
                </Box>

                <Button
                  size="small"
                  variant="contained"
                  disabled={!canGenerateName || nameCountdown > 0}
                  onClick={() => {
                    setLastTriggered("name");
                    startCountdown({ block: "name" });
                  }}
                  sx={{
                    fontWeight: 900,
                    borderRadius: 2,
                    backgroundColor: "rgba(255,255,255,0.22)",
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.3)" },
                  }}
                >
                  {nameCountdown > 0 ? `⏳ ${nameCountdown}` : "Generar"}
                </Button>
              </Box>

              <CardContent>
                <Stack spacing={2}>
                  <TextField label="Tu nombre completo" value={fullName} onChange={(e) => setFullName(e.target.value)} fullWidth />
                  <TextField label="Nombre y apellido de tu padre" value={fatherName} onChange={(e) => setFatherName(e.target.value)} fullWidth />
                  <TextField label="Nombre y apellido de tu madre" value={motherName} onChange={(e) => setMotherName(e.target.value)} fullWidth />

                  <Divider />

                  <Typography sx={{ fontWeight: 800 }}>Resultados</Typography>
                  {showNameResults && luckyByName.length ? (
                    <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                      {luckyByName.map((n) => (
                        <Chip key={n} label={n} sx={{ fontWeight: 900, fontSize: 16, px: 1.2, borderRadius: 2 }} />
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Completa los 3 nombres y presiona <b>Generar</b>.
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* BLOQUE 2 */}
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 3, background: "rgba(255,255,255,0.92)", overflow: "hidden" }}>
              <Box
                sx={{
                  p: 2,
                  background:
                    "linear-gradient(135deg, rgba(76,175,80,1) 0%, rgba(255,152,0,1) 100%)",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <BadgeIcon />
                  <Typography sx={{ fontWeight: 900 }}>Por Cédula (4)</Typography>
                </Box>

                <Button
                  size="small"
                  variant="contained"
                  disabled={!canGenerateCed || cedCountdown > 0}
                  onClick={() => {
                    setLastTriggered("ced");
                    startCountdown({ block: "ced" });
                  }}
                  sx={{
                    fontWeight: 900,
                    borderRadius: 2,
                    backgroundColor: "rgba(255,255,255,0.22)",
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.3)" },
                  }}
                >
                  {cedCountdown > 0 ? `⏳ ${cedCountdown}` : "Generar"}
                </Button>
              </Box>

              <CardContent>
                <Stack spacing={2}>
                  <TextField
                    label="Cédula (11 dígitos)"
                    value={cedula}
                    onChange={(e) => setCedula(e.target.value)}
                    fullWidth
                    inputProps={{ inputMode: "numeric" }}
                    helperText={
                      cedDigits && cedDigits.length !== 11
                        ? `Te faltan ${Math.max(0, 11 - cedDigits.length)} dígitos`
                        : "Solo se usan los dígitos."
                    }
                  />

                  <Divider />

                  <Typography sx={{ fontWeight: 800 }}>Resultados</Typography>
                  {showCedResults && luckyByCedula.length ? (
                    <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                      {luckyByCedula.map((n) => (
                        <Chip key={n} label={n} sx={{ fontWeight: 900, fontSize: 16, px: 1.2, borderRadius: 2 }} />
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Escribe la cédula y presiona <b>Generar</b>.
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* BLOQUE 3 */}
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 3, background: "rgba(255,255,255,0.92)", overflow: "hidden" }}>
              <Box
                sx={{
                  p: 2,
                  background:
                    "linear-gradient(135deg, rgba(244,67,54,1) 0%, rgba(25,118,210,1) 100%)",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CakeIcon />
                  <Typography sx={{ fontWeight: 900 }}>Por Fecha (3) + Desglose</Typography>
                </Box>

                <Button
                  size="small"
                  variant="contained"
                  disabled={!canGenerateDob || dobCountdown > 0}
                  onClick={() => {
                    setLastTriggered("dob");
                    startCountdown({ block: "dob" });
                  }}
                  sx={{
                    fontWeight: 900,
                    borderRadius: 2,
                    backgroundColor: "rgba(255,255,255,0.22)",
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.3)" },
                  }}
                >
                  {dobCountdown > 0 ? `⏳ ${dobCountdown}` : "Generar"}
                </Button>
              </Box>

              <CardContent>
                <Grid container spacing={2} alignItems="stretch">
                  <Grid item xs={12} md={5}>
                    <Stack spacing={2}>
                      <TextField
                        label="Fecha de nacimiento"
                        type="date"
                        value={dob}
                        onChange={(e) => setDob(e.target.value)}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />

                      <Divider />

                      <Typography sx={{ fontWeight: 800 }}>Resultados</Typography>
                      {showDobResults && luckyByDob.length ? (
                        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                          {luckyByDob.map((n) => (
                            <Chip key={n} label={n} sx={{ fontWeight: 900, fontSize: 16, px: 1.2, borderRadius: 2 }} />
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Selecciona la fecha y presiona <b>Generar</b>.
                        </Typography>
                      )}
                    </Stack>
                  </Grid>

                  <Grid item xs={12} md={7}>
                    <Box
                      sx={{
                        height: "100%",
                        borderRadius: 3,
                        p: 2,
                        background:
                          "linear-gradient(180deg, rgba(11,18,32,0.06) 0%, rgba(11,18,32,0.02) 100%)",
                        border: "1px solid rgba(11,18,32,0.08)",
                      }}
                    >
                      <Typography sx={{ fontWeight: 900, mb: 1 }}>Desglose</Typography>
                      {dobInfo ? (
                        <Stack spacing={1}>
                          <Typography>
                            <b>Día:</b> {dobInfo.day} &nbsp; <b>Mes:</b> {dobInfo.month} &nbsp; <b>Año:</b> {dobInfo.year}
                          </Typography>
                          <Typography>
                            <b>Últimos 2 del año:</b> {twoDigits(dobInfo.year2)}
                          </Typography>
                          <Typography>
                            <b>Número de vida (Life Path):</b> {dobInfo.lifePath}
                          </Typography>
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Aquí verás el desglose cuando selecciones la fecha.
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Nota */}
        <Typography
          variant="body2"
          sx={{ mt: 2, color: "rgba(255,255,255,0.7)", textAlign: "center" }}
        >
          Cada vez que presionas <b>Generar</b>, se guarda un nuevo registro en Firestore.
        </Typography>
      </Container>
    </Box>
  );
}
